import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CalendarIcon, ClockIcon, HomeIcon, UserIcon, PhoneIcon, DollarSignIcon, AlertCircleIcon, PercentIcon } from 'lucide-react';
interface ViewingScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (viewingData: {
    leadId: string;
    clientName: string;
    clientPhone: string;
    propertyId: string;
    ownerName: string;
    ownerPhone: string;
    date: string;
    time: string;
    commissionPaidBy: 'owner' | 'client' | 'both';
    ownerCommission?: number;
    clientCommission?: number;
    notes: string;
  }) => void;
  leadData?: {
    leadId: string;
    clientName: string;
    clientPhone: string;
    propertyId?: string;
  };
  editingViewing?: any;
}
export function ViewingScheduleModal({
  isOpen,
  onClose,
  onSubmit,
  leadData,
  editingViewing
}: ViewingScheduleModalProps) {
  const [leadId, setLeadId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [commissionPaidBy, setCommissionPaidBy] = useState<'owner' | 'client' | 'both'>('owner');
  const [ownerCommission, setOwnerCommission] = useState('');
  const [clientCommission, setClientCommission] = useState('');
  const [notes, setNotes] = useState('');
  // Pre-populate form when leadData is provided
  useEffect(() => {
    if (leadData) {
      setLeadId(leadData.leadId);
      setClientName(leadData.clientName);
      setClientPhone(leadData.clientPhone);
      if (leadData.propertyId) {
        setPropertyId(leadData.propertyId);
      }
    }
  }, [leadData]);
  // Pre-populate form when editing
  useEffect(() => {
    if (editingViewing) {
      setLeadId(editingViewing.leadId || '');
      setClientName(editingViewing.clientName || '');
      setClientPhone(editingViewing.clientPhone || '');
      setPropertyId(editingViewing.propertyId || '');
      setOwnerName(editingViewing.ownerName || '');
      setOwnerPhone(editingViewing.ownerPhone || '');
      setDate(editingViewing.date || '');
      setTime(editingViewing.time || '');
      setCommissionPaidBy(editingViewing.commissionPaidBy || 'owner');
      setOwnerCommission(editingViewing.ownerCommission?.toString() || '');
      setClientCommission(editingViewing.clientCommission?.toString() || '');
      setNotes(editingViewing.agentNotes || '');
    }
  }, [editingViewing]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !clientName || !clientPhone || !propertyId || !ownerName || !ownerPhone || !date || !time) {
      alert('Te rog completează toate câmpurile obligatorii');
      return;
    }
    // Validate commission values based on selection
    if (commissionPaidBy === 'owner' && !ownerCommission) {
      alert('Te rog introdu valoarea comisionului pentru proprietar');
      return;
    }
    if (commissionPaidBy === 'client' && !clientCommission) {
      alert('Te rog introdu valoarea comisionului pentru client');
      return;
    }
    if (commissionPaidBy === 'both' && (!ownerCommission || !clientCommission)) {
      alert('Te rog introdu valorile comisionului pentru ambele părți');
      return;
    }
    onSubmit({
      leadId,
      clientName,
      clientPhone,
      propertyId,
      ownerName,
      ownerPhone,
      date,
      time,
      commissionPaidBy,
      ownerCommission: ownerCommission ? parseFloat(ownerCommission) : undefined,
      clientCommission: clientCommission ? parseFloat(clientCommission) : undefined,
      notes
    });
    // Reset form
    setLeadId('');
    setClientName('');
    setClientPhone('');
    setPropertyId('');
    setOwnerName('');
    setOwnerPhone('');
    setDate('');
    setTime('');
    setCommissionPaidBy('owner');
    setOwnerCommission('');
    setClientCommission('');
    setNotes('');
  };
  return <AnimatePresence data-id="element-5955">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-5956" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-5957">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-5958">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-5 flex items-center justify-between" data-id="element-5959">
                <div className="flex items-center space-x-3" data-id="element-5960">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-5961">
                    <CalendarIcon className="w-6 h-6" data-id="element-5962" />
                  </div>
                  <div data-id="element-5963">
                    <h3 className="text-xl font-bold" data-id="element-5964">
                      Programare Vizionare Nouă
                    </h3>
                    <p className="text-sm text-blue-100" data-id="element-5965">
                      Completează toate detaliile necesare
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-5966">
                  <XIcon className="w-6 h-6" data-id="element-5967" />
                </button>
              </div>

              {/* Content */}
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6" data-id="element-5968">
                <div className="space-y-6" data-id="element-5969">
                  {/* Client Info Section */}
                  <div data-id="element-5970">
                    <div className="flex items-center space-x-2 mb-3" data-id="element-5971">
                      <UserIcon className="w-5 h-5 text-blue-600" data-id="element-5972" />
                      <h4 className="font-bold text-gray-900" data-id="element-5973">
                        Informații Client
                      </h4>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 space-y-3" data-id="element-5974">
                      <div data-id="element-5975">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5976">
                          ID Lead *
                        </label>
                        <input type="text" value={leadId} onChange={e => setLeadId(e.target.value)} placeholder="ex: LI00001" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" required readOnly={!!leadData} data-id="element-5977" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5978">
                        <div data-id="element-5979">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5980">
                            Nume Client *
                          </label>
                          <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="ex: Maria Ionescu" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" required readOnly={!!leadData} data-id="element-5981" />
                        </div>
                        <div data-id="element-5982">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5983">
                            Telefon Client *
                          </label>
                          <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="ex: 0732345678" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" required readOnly={!!leadData} data-id="element-5984" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Property & Owner Info Section */}
                  <div data-id="element-5985">
                    <div className="flex items-center space-x-2 mb-3" data-id="element-5986">
                      <HomeIcon className="w-5 h-5 text-purple-600" data-id="element-5987" />
                      <h4 className="font-bold text-gray-900" data-id="element-5988">
                        Informații Proprietate & Proprietar
                      </h4>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 space-y-3" data-id="element-5989">
                      <div data-id="element-5990">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5991">
                          ID Proprietate *
                        </label>
                        <input type="text" value={propertyId} onChange={e => setPropertyId(e.target.value)} placeholder="ex: P98772" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-5992" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-5993">
                        <div data-id="element-5994">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5995">
                            Nume Proprietar *
                          </label>
                          <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="ex: Ion Popescu" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-5996" />
                        </div>
                        <div data-id="element-5997">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5998">
                            Telefon Proprietar *
                          </label>
                          <input type="tel" value={ownerPhone} onChange={e => setOwnerPhone(e.target.value)} placeholder="ex: 0721234567" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-5999" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Viewing Details Section */}
                  <div data-id="element-6000">
                    <div className="flex items-center space-x-2 mb-3" data-id="element-6001">
                      <ClockIcon className="w-5 h-5 text-green-600" data-id="element-6002" />
                      <h4 className="font-bold text-gray-900" data-id="element-6003">
                        Detalii Vizionare
                      </h4>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 space-y-3" data-id="element-6004">
                      <div className="grid grid-cols-2 gap-3" data-id="element-6005">
                        <div data-id="element-6006">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6007">
                            Data *
                          </label>
                          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" required data-id="element-6008" />
                        </div>
                        <div data-id="element-6009">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6010">
                            Ora *
                          </label>
                          <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" required data-id="element-6011" />
                        </div>
                      </div>

                      <div data-id="element-6012">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6013">
                          Comisionare Părți *
                        </label>
                        <div className="grid grid-cols-3 gap-2" data-id="element-6014">
                          <button type="button" onClick={() => setCommissionPaidBy('owner')} className={`px-4 py-3 rounded-lg text-sm font-bold transition-all ${commissionPaidBy === 'owner' ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 border-2 border-gray-200'}`} data-id="element-6015">
                            <DollarSignIcon className="w-4 h-4 mx-auto mb-1" data-id="element-6016" />
                            Proprietar
                          </button>
                          <button type="button" onClick={() => setCommissionPaidBy('client')} className={`px-4 py-3 rounded-lg text-sm font-bold transition-all ${commissionPaidBy === 'client' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 border-2 border-gray-200'}`} data-id="element-6017">
                            <DollarSignIcon className="w-4 h-4 mx-auto mb-1" data-id="element-6018" />
                            Client
                          </button>
                          <button type="button" onClick={() => setCommissionPaidBy('both')} className={`px-4 py-3 rounded-lg text-sm font-bold transition-all ${commissionPaidBy === 'both' ? 'bg-orange-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 border-2 border-gray-200'}`} data-id="element-6019">
                            <DollarSignIcon className="w-4 h-4 mx-auto mb-1" data-id="element-6020" />
                            Ambele
                          </button>
                        </div>
                      </div>

                      {/* Commission Values - Dynamic based on selection */}
                      {(commissionPaidBy === 'owner' || commissionPaidBy === 'both') && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} data-id="element-6021">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6022">
                            Comision Proprietar (%) *
                          </label>
                          <div className="relative" data-id="element-6023">
                            <input type="number" step="0.5" min="0" max="100" value={ownerCommission} onChange={e => setOwnerCommission(e.target.value)} placeholder="ex: 50" className="w-full px-3 py-2 pr-10 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50" required data-id="element-6024" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-600 font-bold" data-id="element-6025">
                              %
                            </span>
                          </div>
                        </motion.div>}

                      {(commissionPaidBy === 'client' || commissionPaidBy === 'both') && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} data-id="element-6026">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6027">
                            Comision Client (%) *
                          </label>
                          <div className="relative" data-id="element-6028">
                            <input type="number" step="0.5" min="0" max="100" value={clientCommission} onChange={e => setClientCommission(e.target.value)} placeholder="ex: 50" className="w-full px-3 py-2 pr-10 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50" required data-id="element-6029" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 font-bold" data-id="element-6030">
                              %
                            </span>
                          </div>
                        </motion.div>}

                      <div data-id="element-6031">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6032">
                          Notițe (opțional)
                        </label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Detalii suplimentare despre vizionare..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-6033" />
                      </div>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="flex items-start space-x-3 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg" data-id="element-6034">
                    <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-6035" />
                    <div data-id="element-6036">
                      <p className="text-sm font-semibold text-amber-900 mb-1" data-id="element-6037">
                        Notă importantă
                      </p>
                      <p className="text-sm text-amber-700" data-id="element-6038">
                        Asigură-te că toate informațiile sunt corecte înainte de
                        a confirma programarea. Valorile comisionului trebuie să
                        fie între 0 și 100%.
                      </p>
                    </div>
                  </div>
                </div>
              </form>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200" data-id="element-6039">
                <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-gray-700 hover:bg-gray-200 transition-colors" data-id="element-6040">
                  Anulează
                </button>
                <button onClick={handleSubmit} className="px-6 py-2.5 rounded-xl font-bold bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg hover:scale-105 transition-all" data-id="element-6041">
                  Confirmă Programare
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}